import { PublicClientApplication, EventType } from "@azure/msal-browser";
import type { AccountInfo } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import { DevBanner } from "./DevBanner";
import { msalConfig } from "./auth/config";
import { clearReauthFlag } from "./auth/renewal";
import { restoreReturnPath } from "./router";
import "./index.css";

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root not found");
}

const msalInstance = new PublicClientApplication(msalConfig);

// MSAL must finish initializing before React renders, otherwise the first
// render races the handling of the redirect response coming back from Entra.
void msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((result) => {
    // A sign-in that came back from Entra is a fresh start for this tab: it
    // may be sent to sign in again if renewal later fails (F197). Until then
    // only a silent success cleared the flag, so a tab that had used its one
    // redirect stayed stuck through every reload.
    if (result) clearReauthFlag();

    // A signed-in account does not automatically become the active one, and
    // every token call needs an active account.
    // Before the first render: a deep link stashed before sign-in is put back
    // so the app starts where the person was going, not at the default screen.
    restoreReturnPath();

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0] ?? null);
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as { account?: AccountInfo };
        if (payload.account) msalInstance.setActiveAccount(payload.account);
      }
    });

    createRoot(container).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <DevBanner />
          <App />
        </MsalProvider>
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    console.error("MSAL failed to initialize", error);
    container.textContent = "Sign-in could not start. Check the console.";
  });
